import {
	Css,
	IContactCardProps,
	Responsive,
	StyledCardContent,
	StyledCardPicture,
} from '@/ui';
import styled from '@emotion/styled';
import BaseCard from '../cards/BaseCard';
import { Color } from '../../../styles/Color';
import { QuickLink } from '../quick-links/QuickLink';
import { ImageOrientation } from '../../../contentful/types/ImageOrientation';
import { getHeadingStyle } from '../../../styles/utils/getHeadingStyle';
import { theme } from '../../../styles/theme';

interface IContactItemProps extends IContactCardProps {
	linkHref: string;
	subtitle?: string;
	imageOrientation?: ImageOrientation;
}

export default function ContactItem({
	linkHref,
	imageSource,
	imageAlt,
	title,
	phoneNumber,
	linkTitle,
	subtitle,
	imageOrientation = 'landscape',
}: IContactItemProps) {
	const isEmail = linkHref.startsWith('mailto:');

	const getLabelValue = () => {
		if (linkTitle) {
			return linkTitle;
		}
		if (isEmail) {
			return 'Send email';
		}
		return 'Go to website';
	};

	const label = getLabelValue();

	return (
		<BaseCard linkHref={linkHref} fullyClickable={false}>
			<CardContentStyled>
				{imageOrientation === 'portrait' ? (
					<CardPicturePortraitWrapper>
						<CardPictureStyled source={imageSource} alt={imageAlt} />
					</CardPicturePortraitWrapper>
				) : (
					<CardPictureWrapper>
						<CardPictureStyled source={imageSource} alt={imageAlt} />
					</CardPictureWrapper>
				)}

				<CardContent>
					<div>
						<CardContentTitle aria-label={title}>{title}</CardContentTitle>
						{subtitle && <CardContentSubtitle>{subtitle}</CardContentSubtitle>}
					</div>

					<div>
						{phoneNumber && <StyledLinkText>{phoneNumber}</StyledLinkText>}

						{linkHref && (
							<QuickLink
								type={isEmail ? 'email' : 'external'}
								label={label}
								url={linkHref}
							/>
						)}
					</div>
				</CardContent>
			</CardContentStyled>
		</BaseCard>
	);
}

const CardContentStyled = styled(StyledCardContent)`
	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		justify-content: flex-start;
	}
`;

const CardContent = styled.div`
	min-height: 120px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	flex: 1;

	h3 {
		margin-bottom: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		justify-content: flex-start;
		min-height: auto;

		h3 {
			font-size: ${Css.pxToRemFromTheme(16)};
		}
	}
`;

const CardPictureWrapper = styled.div`
	background-color: ${Color.Neutral94};
	border-top-left-radius: ${Css.pxToRem(16)};
	border-top-right-radius: ${Css.pxToRem(16)};
	aspect-ratio: 3/1;

	overflow: hidden;
	margin-bottom: ${Css.pxToRem(16)};
`;

const CardPicturePortraitWrapper = styled.div`
	background-color: ${Color.Neutral94};
	width: 180px;
	height: 220px;
	overflow: hidden;
	margin-bottom: ${Css.pxToRem(16)};
`;

const CardPictureStyled = styled(StyledCardPicture)`
	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`;

const CardContentTitle = styled.h3`
	${getHeadingStyle(theme, 'h6')}
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	margin: 0;
	text-overflow: ellipsis;
`;

const CardContentSubtitle = styled.div`
	margin-bottom: ${Css.pxToRemFromTheme(16)};
`;

const StyledLinkText = styled.div`
	letter-spacing: ${Css.pxToRemFromTheme(0.64)};
	margin-bottom: ${Css.pxToRem(5)};
`;
